import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface ActionItem {
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void | Promise<void>;
  className?: string;
  disabled?: boolean;
  divider?: boolean;
  groupHeader?: boolean;  // เป็น header ของกลุ่ม
  isDanger?: boolean;  // ใช้สำหรับ style ปุ่มที่เป็นอันตราย (เช่น ลบ)
  visible?: boolean;   // ถ้า false จะไม่แสดงรายการนี้ (default true)
  variant?: 'default' | 'danger' | 'warning' | 'success' | 'info';
}

/**
 * BUG-S2610-001: เดิม `variant` ถูกประกาศใน ActionItem แต่ render อ่านเฉพาะ `isDanger`
 * → ผู้เรียกที่ส่ง `variant: 'danger'` (เมนู "ลบ" ใบส่งซ่อม / แพ็กเกจซิม) และ 'success'/'warning'
 * (หน้าสัญญาเช่า) ได้สีเทาเหมือนเมนูทั่วไป — ของอันตรายไม่เด่น · map สีให้ครบทุก variant ที่ type รองรับ
 */
export const ACTION_VARIANT_CLASS: Record<NonNullable<ActionItem['variant']>, string> = {
  default: 'text-secondary-700 hover:bg-secondary-50 hover:text-secondary-900',
  danger: 'text-danger-600 hover:bg-danger-50 hover:text-danger-700',
  warning: 'text-warning-700 hover:bg-warning-50 hover:text-warning-800',
  success: 'text-success-700 hover:bg-success-50 hover:text-success-800',
  info: 'text-primary-700 hover:bg-primary-50 hover:text-primary-800',
};

interface ActionDropdownProps {
  actions: ActionItem[];
  buttonLabel?: string;
  buttonIcon?: React.ReactNode;
  buttonClassName?: string;
  menuClassName?: string;
  position?: 'left' | 'right';
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  actions = [],
  buttonLabel,
  buttonIcon,
  buttonClassName = '',
  menuClassName = '',
  position = 'right'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // คำนวณตำแหน่งของ dropdown
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      const dropdownWidth = 192;
      const dropdownHeight = 200;
      
      // คำนวณตำแหน่ง dropdown ให้อยู่ในพื้นที่ที่มองเห็นได้
      let top = buttonRect.bottom + scrollTop + 4; // 4px spacing
      let left;
      
      // ตรวจสอบพื้นที่ด้านขวา
      const spaceOnRight = window.innerWidth - buttonRect.right;
      // ตรวจสอบพื้นที่ด้านซ้าย  
      const spaceOnLeft = buttonRect.left;
      
      // เลือกด้านที่มีพื้นที่มากกว่าและสอดคล้องกับ position prop
      if (position === 'left' || (position === 'right' && spaceOnRight < dropdownWidth)) {
        // แสดงทางซ้าย
        left = buttonRect.right + scrollLeft - dropdownWidth;
      } else {
        // แสดงทางขวา (default)
        left = buttonRect.left + scrollLeft;
      }
      
      // ปรับให้อยู่ภายในหน้าจอ
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = window.innerWidth - dropdownWidth - 8;
      }
      if (left < 8) {
        left = 8;
      }
      
      // ตรวจสอบว่า dropdown จะล้นหน้าจอด้านล่างหรือไม่
      if (top + dropdownHeight > window.innerHeight + scrollTop - 8) {
        // แสดงด้านบนของปุ่มแทน
        top = buttonRect.top + scrollTop - dropdownHeight - 4;
      }
      
      setDropdownPosition({ top, left });
    }
  }, [isOpen, position]);

  // ปิด dropdown เมื่อคลิกข้างนอก และอัพเดทตำแหน่งเมื่อ scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // BUG-A11Y-ADROP-001 FIX (a11y, WCAG 2.1.1 Keyboard / expected menu dismissal):
    // the dropdown had outside-click-to-close but NO Escape-to-close. A keyboard-only
    // user who opened the menu with Enter could not dismiss it with Escape (had to Tab
    // away or click). Add an Escape handler that closes the menu and returns focus to
    // the persistent toggle button (WCAG 2.4.3 focus order — mirrors handleActionClick).
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    const handleScroll = () => {
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        const dropdownWidth = 192;
        const dropdownHeight = 200;
        
        // คำนวณตำแหน่ง dropdown ให้อยู่ในพื้นที่ที่มองเห็นได้
        let top = buttonRect.bottom + scrollTop + 4;
        let left;
        
        // ตรวจสอบพื้นที่ด้านขวา
        const spaceOnRight = window.innerWidth - buttonRect.right;
        
        // เลือกด้านที่มีพื้นที่มากกว่าและสอดคล้องกับ position prop
        if (position === 'left' || (position === 'right' && spaceOnRight < dropdownWidth)) {
          // แสดงทางซ้าย
          left = buttonRect.right + scrollLeft - dropdownWidth;
        } else {
          // แสดงทางขวา (default)
          left = buttonRect.left + scrollLeft;
        }
        
        // ปรับให้อยู่ภายในหน้าจอ
        if (left + dropdownWidth > window.innerWidth - 8) {
          left = window.innerWidth - dropdownWidth - 8;
        }
        if (left < 8) {
          left = 8;
        }
        
        // ตรวจสอบว่า dropdown จะล้นหน้าจอด้านล่างหรือไม่
        if (top + dropdownHeight > window.innerHeight + scrollTop - 8) {
          top = buttonRect.top + scrollTop - dropdownHeight - 4;
        }
        
        setDropdownPosition({ top, left });
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, position]);

  // KD3 S2043 (a11y, WCAG 2.1.1 Keyboard): เมนูถูก portal ไปท้าย <body> — เปิดด้วย Enter แล้ว
  // โฟกัสค้างที่ปุ่ม กด Tab ไปแถวถัดไป ลูกศรก็ไม่มีผล → ผู้ใช้คีย์บอร์ดเลือกรายการในเมนูไม่ได้เลย
  // (เช่น "ยกเลิก (ออกใบลดหนี้)") · แก้ตาม WAI-ARIA menu button: เปิดแล้วโฟกัสรายการแรกที่กดได้
  // ลูกศรขึ้น/ลง Home/End วนรายการ · Tab ปิดเมนูแล้วคืนโฟกัสปุ่ม (ลำดับ Tab ต่อจากปุ่มเดิม)
  const menuItems = () =>
    Array.from(dropdownRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);

  useEffect(() => {
    if (isOpen) menuItems()[0]?.focus();
  }, [isOpen]);

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    const items = menuItems();
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    let next: number | null = null;
    if (event.key === 'ArrowDown') next = (idx + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (idx - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'Tab') {
      buttonRef.current?.focus();
      setIsOpen(false);
      return;
    }
    if (next !== null) {
      event.preventDefault();
      items[next].focus();
    }
  };

  const handleActionClick = (action: ActionItem) => {
    if (!action.disabled && !action.divider) {
      // BUG-S57-001 FIX (a11y, WCAG 2.4.3): move focus to the persistent toggle button
      // BEFORE running the action. The action often opens a modal, which captures
      // document.activeElement as its focus-return target. Without this, it captures
      // the transient menu item (unmounts when the dropdown closes) → on modal close
      // focus is restored to a detached node → lands on <body>. Focusing the toggle
      // first means the modal captures the persistent button and returns focus to it.
      buttonRef.current?.focus();
      setIsOpen(false);
      action.onClick();
    }
  };

  return (
    <>
      <div className="relative inline-block text-left">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`inline-flex items-center justify-center rounded-lg border border-secondary-200 bg-white w-11 h-11 sm:w-9 sm:h-9 text-secondary-500 hover:bg-secondary-50 hover:text-secondary-700 hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-1 transition-all duration-150 ${buttonClassName}`}
          aria-label={buttonLabel || 'เมนูจัดการ'}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          {buttonIcon || (
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          )}
          {buttonLabel && <span className="ml-1">{buttonLabel}</span>}
        </button>
      </div>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 9999
          }}
          className={`w-48 rounded-xl bg-white shadow-dropdown border border-secondary-200 overflow-hidden ${menuClassName}`}
          role="menu"
          aria-label={buttonLabel || 'เมนูจัดการ'}
          onKeyDown={handleMenuKeyDown}
        >
          <div className="py-1.5">
            {actions && actions.length > 0 ? (
              actions.filter(a => a.visible !== false).map((action, index) => {
                // Group Header - แสดงหัวข้อกลุ่ม
                if (action.groupHeader) {
                  return (
                    <div
                      key={index}
                      className="px-3 py-1.5 text-xs font-semibold text-secondary-400 bg-secondary-50/80 border-b border-secondary-100"
                    >
                      <div className="flex items-center gap-1.5">
                        {action.icon && <span className="opacity-60">{action.icon}</span>}
                        {action.label}
                      </div>
                    </div>
                  );
                }

                if (action.divider) {
                  return <hr key={index} className="my-1 border-secondary-100" />;
                }

                return (
                  <button
                    key={index}
                    type="button"
                    role="menuitem"
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                    className={`flex w-full items-center px-3.5 py-2 text-sm text-left gap-2.5 transition-colors duration-100 focus:outline-none focus-visible:bg-secondary-100 ${
                      action.disabled
                        ? 'text-secondary-300 cursor-not-allowed'
                        : ACTION_VARIANT_CLASS[action.isDanger ? 'danger' : (action.variant || 'default')]
                    } ${action.className || ''}`}
                  >
                    {action.icon && (
                      <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center opacity-75">
                        {action.icon}
                      </span>
                    )}
                    {action.label}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-2 text-sm text-secondary-400">ไม่มีการดำเนินการ</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ActionDropdown;